function nonBlank(raw: unknown): boolean {
  return typeof raw === 'string' && raw.trim() !== ''
}

function trimOrEmpty(raw: unknown): string {
  return nonBlank(raw) ? String(raw).trim() : ''
}

export function isRootPassLabel(passLabel: string): boolean {
  return passLabel.trim() === 'Root'
}

/** Fill 2+ / Cap 2+ — dual-torch secondary; temps recorded on the primary pass. */
export function isSecondarySimultaneousPass(passLabel: string): boolean {
  const t = passLabel.trim()
  const fill = t.match(/^Fill\s+(\d+)$/i)
  if (fill) return Number(fill[1]) >= 2
  const cap = t.match(/^Cap\s+(\d+)$/i)
  if (cap) return Number(cap[1]) >= 2
  return false
}

/**
 * Midpoint of preheat/interpass bounds when both parse as finite numbers.
 */
export function computePreheatMidpoint(min: string, max: string): number | null {
  const minText = min.trim()
  const maxText = max.trim()
  if (!minText || !maxText) return null
  const minNum = Number(minText)
  const maxNum = Number(maxText)
  if (!Number.isFinite(minNum) || !Number.isFinite(maxNum)) return null
  return (minNum + maxNum) / 2
}

/**
 * Pipe-set preheat section values for one pass row in welding-parameters
 * matrices: Root uses preheatMin/Max; all other passes use interpassMin/Max.
 *
 * When `weldOverride` is provided (including both-empty), it wins over the
 * WPS preheat section. Fill 2+ / Cap 2+ without an override stay blank.
 */
export function resolvePreheatCellsForPass(
  preheatSection: Record<string, unknown> | null | undefined,
  passLabel: string,
  weldOverride?: { min?: string; max?: string; avg?: string } | null
): { min: string; max: string; avg: number | null } {
  if (weldOverride != null) {
    const bounds = {
      min: trimOrEmpty(weldOverride.min),
      max: trimOrEmpty(weldOverride.max)
    }
    const overrideAvg = trimOrEmpty(weldOverride.avg)
    const avgNum = overrideAvg ? Number(overrideAvg) : NaN
    return {
      ...bounds,
      avg: Number.isFinite(avgNum) ? avgNum : computePreheatMidpoint(bounds.min, bounds.max)
    }
  }
  if (isSecondarySimultaneousPass(passLabel)) {
    return { min: '', max: '', avg: null }
  }
  const preheat = preheatSection ?? {}
  const bounds = isRootPassLabel(passLabel)
    ? {
        min: trimOrEmpty(preheat.preheatMin),
        max: trimOrEmpty(preheat.preheatMax)
      }
    : {
        min: trimOrEmpty(preheat.interpassMin),
        max: trimOrEmpty(preheat.interpassMax)
      }
  return {
    ...bounds,
    avg: computePreheatMidpoint(bounds.min, bounds.max)
  }
}
